import { z } from 'zod';

/**
 * The background effects an organiser can pick for the public event page,
 * in picker order, and how values saved before the October 2026 refresh
 * read today.
 *
 * Stored in events.metadata.landingThemeBackgroundEffect. Five effects were
 * retired from the picker; events that saved one keep working because every
 * reader goes through normaliseBackgroundEffect, which maps each to the new
 * effect closest in spirit.
 */
export const BACKGROUND_EFFECTS = [
  'none',
  'accent-glow',
  'logo-watermark',
  'lanes',
  'topo',
  'sprint',
  'chalk',
  'outline',
  'halftone',
] as const;

export type BackgroundEffect = (typeof BACKGROUND_EFFECTS)[number];

/** Retired values, and what each now draws. */
export const LEGACY_BACKGROUND_EFFECTS = {
  grid: 'lanes',
  particles: 'sprint',
  'gradient-mesh': 'chalk',
  vignette: 'halftone',
  aurora: 'accent-glow',
} as const satisfies Record<string, BackgroundEffect>;

export type LegacyBackgroundEffect = keyof typeof LEGACY_BACKGROUND_EFFECTS;

/** What the route stores, and every reader shows, when nothing is chosen. */
export const DEFAULT_BACKGROUND_EFFECT: BackgroundEffect = 'accent-glow';

const CURRENT = new Set<string>(BACKGROUND_EFFECTS);

/** Any stored or sent value as an effect the page can draw today. */
export function normaliseBackgroundEffect(value: unknown): BackgroundEffect {
  if (typeof value !== 'string' || !value) return DEFAULT_BACKGROUND_EFFECT;
  if (CURRENT.has(value)) return value as BackgroundEffect;
  if (Object.prototype.hasOwnProperty.call(LEGACY_BACKGROUND_EFFECTS, value)) {
    return LEGACY_BACKGROUND_EFFECTS[value as LegacyBackgroundEffect];
  }
  // Anything else (a proposal that never shipped, such as pulse, or a bad row)
  // draws nothing, as the page always did for a value it did not know.
  return 'none';
}

/** The picker: every effect in order, with the line a tile can show under its name. */
export const EFFECT_OPTIONS: ReadonlyArray<{ value: BackgroundEffect; label: string; note: string }> = [
  { value: 'none', label: 'Plain', note: 'Your background colour only' },
  { value: 'accent-glow', label: 'Accent glow', note: 'A soft glow in your accent' },
  { value: 'logo-watermark', label: 'Logo watermark', note: 'Your logo, large and faint' },
  { value: 'lanes', label: 'Lane lines', note: 'The bend of a running track' },
  { value: 'topo', label: 'Topo', note: 'Contour lines, unique to your event' },
  { value: 'sprint', label: 'Sprint', note: 'Speed streaks across the top' },
  { value: 'chalk', label: 'Chalk', note: 'Chalk dust and a tally of reps' },
  { value: 'outline', label: 'Outline type', note: 'A huge outlined word in your heading font' },
  { value: 'halftone', label: 'Halftone', note: 'Print dots from the corner' },
];

export function effectLabel(value: unknown): string {
  const v = normaliseBackgroundEffect(value);
  return EFFECT_OPTIONS.find((o) => o.value === v)?.label ?? 'Accent glow';
}

/**
 * The effect as the save route takes it. Absent, null or empty means the
 * route's default (accent glow), as before. A retired value from an older
 * form is stored as its replacement; anything else is refused rather than
 * stored, since the public page could not draw it.
 */
export const backgroundEffectSchema = z
  .union([
    z.enum(BACKGROUND_EFFECTS),
    z.enum(Object.keys(LEGACY_BACKGROUND_EFFECTS) as [LegacyBackgroundEffect, ...LegacyBackgroundEffect[]]),
    z.literal(''),
    z.null(),
    z.undefined(),
  ])
  .transform((v) => (v ? normaliseBackgroundEffect(v) : undefined));

// ── Logo watermark placement ────────────────────────────────────────────────

export const WATERMARK_PLACEMENTS = ['left', 'centre', 'right'] as const;
export type WatermarkPlacement = (typeof WATERMARK_PLACEMENTS)[number];
export const DEFAULT_WATERMARK_PLACEMENT: WatermarkPlacement = 'right';

export const WATERMARK_PLACEMENT_OPTIONS: ReadonlyArray<{ value: WatermarkPlacement; label: string }> = [
  { value: 'left', label: 'Left' },
  { value: 'centre', label: 'Centre' },
  { value: 'right', label: 'Right' },
];

/** Stored as landingThemeWatermarkPlacement; absent or unknown reads as right, where it always sat. */
export function normaliseWatermarkPlacement(value: unknown): WatermarkPlacement {
  return (WATERMARK_PLACEMENTS as readonly unknown[]).includes(value) ? (value as WatermarkPlacement) : DEFAULT_WATERMARK_PLACEMENT;
}

/** Optional on save: a caller that does not send it (older forms, GTM) keeps what is stored. */
export const watermarkPlacementSchema = z.enum(WATERMARK_PLACEMENTS).optional();

// ── Outline type word ───────────────────────────────────────────────────────

/** The longest word the save route takes. */
export const OUTLINE_WORD_MAX = 16;
/** The longest word the Branding and wizard field lets an organiser type: longer draws too small to read. */
export const OUTLINE_WORD_FIELD_MAX = 12;

/** Letters (any alphabet), digits, spaces, & . and - only. */
const OUTLINE_WORD_CHARS = /^[\p{L}\d &.-]*$/u;
const OUTLINE_WORD_STRIP = /[^\p{L}\d &.-]/gu;

/**
 * As the field shows it while the organiser types: anything the route would
 * refuse dropped, upper case as drawn, no longer than the field allows.
 * Spaces are kept as typed so a second word can be started.
 */
export function outlineWordDraft(value: string): string {
  return value.replace(OUTLINE_WORD_STRIP, '').toUpperCase().slice(0, OUTLINE_WORD_FIELD_MAX);
}

/**
 * The organiser's own word (landingThemeOutlineWord) as Outline type draws
 * it: allowed characters only, single spaces, trimmed, upper case. Anything
 * else, or nothing left, is '' and the automatic word is drawn instead.
 */
export function cleanOutlineWord(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.replace(OUTLINE_WORD_STRIP, '').replace(/\s+/g, ' ').trim().toUpperCase().slice(0, OUTLINE_WORD_MAX).trim();
}

/**
 * The word as the save route takes it. Absent keeps what is stored; null or
 * empty clears it (back to the automatic word); otherwise a trimmed word of
 * at most 16 letters, digits, spaces, & . or -, stored in upper case.
 */
export const outlineWordSchema = z
  .union([
    z
      .string()
      .trim()
      .max(OUTLINE_WORD_MAX, `Keep the word to ${OUTLINE_WORD_MAX} characters or fewer`)
      .regex(OUTLINE_WORD_CHARS, 'Use letters, numbers, spaces, & . or - only'),
    z.null(),
  ])
  .optional()
  .transform((v) => (v === undefined ? undefined : v ? v.replace(/\s+/g, ' ').toUpperCase() : null));
